import { useEffect, useRef } from "react";
import type { RefObject } from "react";

// Dismiss a dropdown / popover when a pointer press lands outside it. Attach the
// returned ref to the controller's outer wrapper and pass `active` = is-open, so
// the document listener is only bound while the panel is showing. This gives our
// custom dropdown controllers the same "click away to close" behaviour a native
// <select> has for free. The callback is held in a ref so passing an inline
// `() => setOpen(false)` doesn't re-bind the listener every render.
// `also` counts other elements as inside - a panel rendered elsewhere in a portal - and
// so does any element matching `alsoSelector`.
export const useClickOutside = <T extends HTMLElement = HTMLDivElement>(
  active: boolean,
  onOutside: () => void,
  also: RefObject<HTMLElement>[] = [],
  alsoSelector?: string,
): RefObject<T> => {
  const ref = useRef<T>(null);
  const onOutsideRef = useRef(onOutside);
  onOutsideRef.current = onOutside;
  const alsoRef = useRef(also);
  alsoRef.current = also;

  useEffect(() => {
    if (!active) return;
    // A tap, not the start of a scroll: close on release, where the press began
    // outside and barely moved. pointer events, not mouse ones: iOS sends no mouse
    // events for a tap on plain text.
    let pressed: { x: number; y: number } | null = null;
    const outside = (e: PointerEvent) =>
      !!ref.current &&
      !(alsoSelector && (e.target as Element | null)?.closest?.(alsoSelector)) &&
      [ref, ...alsoRef.current].every((r) => !r.current || !r.current.contains(e.target as Node));
    const onDown = (e: PointerEvent) => {
      pressed = outside(e) ? { x: e.clientX, y: e.clientY } : null;
    };
    const onUp = (e: PointerEvent) => {
      if (pressed && Math.hypot(e.clientX - pressed.x, e.clientY - pressed.y) < 10) {
        onOutsideRef.current();
      }
      pressed = null;
    };
    document.addEventListener("pointerdown", onDown);
    document.addEventListener("pointerup", onUp);
    return () => {
      document.removeEventListener("pointerdown", onDown);
      document.removeEventListener("pointerup", onUp);
    };
  }, [active, alsoSelector]);

  return ref;
};
